import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, Sun, Moon, Sparkle, Plus, Drop, Planet, UsersThree, BookBookmark, GearSix } from "@phosphor-icons/react";
import { useTheme } from "./ThemeProvider";
import { Mascot } from "./Mascot";
import { cn } from "@/lib/utils";

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="group flex min-h-[48px] w-full items-center gap-3 rounded-full border border-border bg-muted/60 p-1.5 pr-4 transition-colors hover:border-primary/40"
    >
      <span className={cn("flex h-9 w-9 items-center justify-center rounded-full transition-all duration-300 ease-bounce", isDark ? "bg-card text-secondary" : "bg-primary text-primary-foreground")}>
        {isDark ? <Moon size={18} weight="fill" /> : <Sun size={18} weight="fill" />}
      </span>
      <span className="text-sm font-bold text-foreground">{isDark ? "Dark mode" : "Light mode"}</span>
    </button>
  );
}

function ModeSwitch() {
  const { pathname } = useLocation();
  const mode = pathname.startsWith("/library")
    ? "library"
    : pathname.startsWith("/architecture")
      ? "architecture"
      : "foundations";
  const base = "flex min-h-[40px] items-center justify-center rounded-full px-2 text-xs font-bold transition-all";
  const on = "bg-card text-primary-strong shadow-soft";
  const off = "text-muted-foreground hover:text-foreground";
  return (
    <div className="grid grid-cols-3 gap-1 rounded-full bg-muted p-1.5">
      <Link to="/" data-testid="mode-foundations" className={cn(base, mode === "foundations" ? on : off)}>Foundations</Link>
      <Link to="/library" data-testid="mode-library" className={cn(base, mode === "library" ? on : off)}>Library</Link>
      <Link to="/architecture" data-testid="mode-architecture" className={cn(base, mode === "architecture" ? on : off)}>Journeys</Link>
    </div>
  );
}

function NavList({ items, active, onClick }) {
  return (
    <nav className="flex flex-col gap-1" aria-label="Sections">
      {items.map(({ id, label, icon: Icon }) => {
        const isActive = active === id;
        return (
          <a
            key={id}
            href={`#${id}`}
            onClick={onClick}
            data-testid={`nav-${id}`}
            aria-current={isActive ? "true" : undefined}
            className={cn(
              "flex min-h-[48px] items-center gap-3 rounded-2xl px-3.5 text-base font-bold transition-all duration-200",
              isActive ? "bg-primary/12 text-primary-strong" : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <span className={cn("flex h-8 w-8 items-center justify-center rounded-xl transition-colors", isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
              <Icon size={18} weight={isActive ? "fill" : "bold"} />
            </span>
            {label}
          </a>
        );
      })}
    </nav>
  );
}

const PLAYGROUND_MATHS = [
  { to: "/experiences/year1/addition", label: "Addition Story Adventure", testid: "nav-playground-addition", icon: Plus },
  { to: "/experiences/year1/water-the-plant", label: "Water the Plant", testid: "nav-playground-water", icon: Drop },
];

function PlaygroundNav({ onClick }) {
  const { pathname } = useLocation();
  return (
    <div className="mt-7">
      <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Playground</p>
      <div className="rounded-2xl bg-muted/40 p-2">
        <div className="flex items-center gap-2 px-2 py-1 text-sm font-extrabold text-foreground">
          <Sparkle size={16} weight="fill" className="text-secondary" /> Year 1
        </div>
        <div className="ml-3 border-l-2 border-border pl-3">
          <Link
            to="/select-child"
            onClick={onClick}
            data-testid="nav-playground-select-child"
            aria-current={pathname.startsWith("/select-child") ? "page" : undefined}
            className={cn(
              "flex min-h-[44px] items-center gap-2 rounded-xl px-2.5 text-sm font-bold transition-all duration-200",
              pathname.startsWith("/select-child") ? "bg-primary/12 text-primary-strong" : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg transition-colors", pathname.startsWith("/select-child") ? "bg-primary text-primary-foreground" : "bg-card text-primary")}>
              <UsersThree size={15} weight="bold" />
            </span>
            Select Child
          </Link>
          <Link
            to="/home/year1"
            onClick={onClick}
            data-testid="nav-playground-home"
            aria-current={pathname.startsWith("/home/year1") ? "page" : undefined}
            className={cn(
              "flex min-h-[44px] items-center gap-2 rounded-xl px-2.5 text-sm font-bold transition-all duration-200",
              pathname.startsWith("/home/year1") ? "bg-primary/12 text-primary-strong" : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg transition-colors", pathname.startsWith("/home/year1") ? "bg-primary text-primary-foreground" : "bg-card text-primary")}>
              <Planet size={15} weight="bold" />
            </span>
            Home World
          </Link>
          <p className="px-1 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Maths</p>
          {PLAYGROUND_MATHS.map(({ to, label, testid, icon: Icon }) => {
            const active = pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                onClick={onClick}
                data-testid={testid}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex min-h-[44px] items-center gap-2 rounded-xl px-2.5 text-sm font-bold transition-all duration-200",
                  active ? "bg-primary/12 text-primary-strong" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg transition-colors", active ? "bg-primary text-primary-foreground" : "bg-card text-primary")}>
                  <Icon size={15} weight="bold" />
                </span>
                {label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Brand({ subtitle }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent">
        <Mascot size={34} animated={false} />
      </div>
      <div>
        <p className="font-display text-xl font-extrabold leading-none tracking-tight">Bloom</p>
        <p className="mt-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

export function Sidebar({ items, subtitle = "Design System" }) {
  const [active, setActive] = useState(items[0]?.id);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 },
    );
    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items]);

  return (
    <>
      <aside className="fixed left-0 top-0 z-30 hidden h-screen w-72 flex-col border-r border-border bg-card/80 px-5 py-7 backdrop-blur-xl lg:flex">
        <Brand subtitle={subtitle} />
        <div className="mt-5">
          <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Foundations</p>
          <ModeSwitch />
        </div>
        <PlaygroundNav />
        <div className="scroll-soft mt-5 flex-1 overflow-y-auto pr-1">
          <NavList items={items} active={active} />
        </div>
        <div className="mt-4 border-t border-border pt-4"><ThemeToggle /></div>
      </aside>

      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-card/85 px-4 py-3 backdrop-blur-xl lg:hidden">
        <Brand subtitle={subtitle} />
        <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-open" className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted text-foreground">
          <List size={24} weight="bold" />
        </button>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              className="absolute right-0 top-0 flex h-full w-[82%] max-w-sm flex-col bg-card px-5 py-6 shadow-soft-lg"
            >
              <div className="flex items-center justify-between">
                <Brand subtitle={subtitle} />
                <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="flex h-11 w-11 items-center justify-center rounded-2xl bg-muted text-foreground">
                  <X size={22} weight="bold" />
                </button>
              </div>
              <div className="mt-5">
                <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Foundations</p>
                <ModeSwitch />
              </div>
              <PlaygroundNav onClick={() => setOpen(false)} />
              <div className="scroll-soft mt-5 flex-1 overflow-y-auto">
                <NavList items={items} active={active} onClick={() => setOpen(false)} />
              </div>
              <div className="mt-4 border-t border-border pt-4"><ThemeToggle /></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Sidebar;
